"use client"

import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { type ReactNode, useState, useEffect } from "react"
import { State } from "wagmi"
import { WagmiProvider } from "@privy-io/wagmi"
import { TooltipProvider } from "@/components/ui/tooltip"
import { getConfig } from "util/wagmiConfig"
import { BreadcrumbContextProvider } from "./contexts/BreadcrumbContext"
import { initializeAmplitude } from "@/lib/utils/amplitude"
import { identifySentryByCookies } from "@/lib/utils/sentry"
import { PrivyProvider } from "@privy-io/react-auth"
import { useTheme } from "next-themes"
import { ThemeProvider } from "@/app/components/ThemeProvider"
import { isDev } from "@/lib/api/constants"
import { isCustomDomain } from "@/middleware"

type Props = {
  children: ReactNode
  initialWagmiState: State | undefined
}

const config = getConfig({ enableSSR: false })

/**
 * For proper Wagmi type-safety throughout
 * the codebase.
 */
declare module "wagmi" {
  interface Register {
    config: typeof config
  }
}

export default function ProvidersWrappedInTheme({
  children,
  initialWagmiState,
}: Props) {
  return (
    <>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <Providers initialWagmiState={initialWagmiState}>{children}</Providers>
      </ThemeProvider>
    </>
  )
}

function Providers({ children, initialWagmiState }: Props) {
  const { resolvedTheme: theme } = useTheme()
  const [queryClient] = useState(() => new QueryClient())

  useEffect(() => {
    initializeAmplitude()
    identifySentryByCookies()
  }, [])

  {
    /** TODO(PAR-3122): Re-enable SSR when issue is fixed. **/
  }

  let clientId

  /**
   * If the current domain is a custom domain we need to
   * use a Privy client that allows all domains
   **/
  if (typeof window !== "undefined" && isCustomDomain(window.location.hostname))
    clientId = "client-WY5daoHB2QZtyi2rRQjhRomhuW9c8jFPX4Shf6Nk9auNH"

  /**
   * If not forcing Privy dev app (dev Next and dev API) but still in dev (dev Next against prod API),
   * we need to use the dev client (linked to the prod Firestore db)
   */
  if (!process.env.NEXT_PUBLIC_PRIVY_DEV && isDev)
    clientId = "client-WY5daoHB2QZtyi2rRQjhRomhuW9c8jFPX4SXgaEsbrFow"

  return (
    <TooltipProvider>
      <BreadcrumbContextProvider>
        <PrivyProvider
          /**
           * Default to Privy production app with optional dev Privy app override
           * which uses the Privy dev app (linked to the dev Firestore db)
           **/
          appId={
            process.env.NEXT_PUBLIC_PRIVY_DEV
              ? "cmah345kg001nl40l3jm3ybl5"
              : "cm3dme51k0111lckn5dina2i0"
          }
          clientId={clientId}
          config={{
            appearance: {
              theme: theme === "dark" ? "dark" : "light",
              accentColor: "#676FFF",
              logo: `https://paragraph.com/branding/v2/${theme === "dark" ? "white" : "black"}.png`,
            },
          }}
        >
          <QueryClientProvider client={queryClient}>
            <WagmiProvider config={config} initialState={initialWagmiState}>
              {children}
            </WagmiProvider>
          </QueryClientProvider>
        </PrivyProvider>
      </BreadcrumbContextProvider>
    </TooltipProvider>
  )
}
